import {
  forwardRef,
  InputHTMLAttributes,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import { Check } from '@design-system/icons';
import {
  COUNTRY_DIAL_CODES,
  isCountryFlagHidden,
  isISOCountryCode,
  ISOCountryCode,
  localizedCountryName,
  parseCountryCode,
} from '@foundation/countries';
import { cn } from '@foundation/utils/cn';
import { CountryFlag } from '@shared/ui';
import { ErrorMessage } from '@shared/ui/components/inputs/ErrorMessage';

import { AdvancedSelect } from './AdvancedSelect';
import { SearchableSelect } from './SearchableSelect';

export interface PhoneInputProps
  extends Omit<
    InputHTMLAttributes<HTMLInputElement>,
    'value' | 'onChange' | 'name'
  > {
  name: string;
  label?: string;
  error?: string;
  countryCode?: ISOCountryCode;
  phoneNumber: string;
  onCountryCodeChange: (countryCode: ISOCountryCode) => void;
  onPhoneNumberChange: (phoneNumber: string) => void;
  locale: string;
  texts: {
    selectCountry: string;
    searchPlaceholder: string;
    noCountries: string;
  };
  showRequired?: boolean;
  /** Show flags in the dial-code option list. The selected country keeps its flag regardless. */
  showOptionFlags?: boolean;
}

type OptionRendererProps = {
  option: { value: ISOCountryCode; label: string };
  isSelected: boolean;
  onSelect: (value: ISOCountryCode) => void;
  ref?: (el: HTMLButtonElement | null) => void;
  isFocused?: boolean;
  onFocus?: () => void;
  'data-testid'?: string;
};

const createOptionRenderer =
  (showFlag: boolean) =>
  ({
    ref,
    onFocus,
    'data-testid': dataTestId,
    option,
    isSelected,
    isFocused,
    onSelect,
  }: OptionRendererProps) =>
    (
      <button
        ref={ref}
        type="button"
        onMouseDown={() => onSelect(option.value)}
        onFocus={onFocus}
        className={cn(
          'w-full pl-6 pr-4 py-3 flex items-center gap-2 hover:bg-gray-100 transition-colors text-left focus:outline-none',
          isSelected && 'bg-alert-positive-primary',
          isFocused && 'bg-gray-200',
        )}
        data-testid={dataTestId ? dataTestId : undefined}
      >
        {showFlag && (
          <div className="shrink-0">
            <CountryFlag countryCode={option.value} />
          </div>
        )}
        <span className="text-base">{option.label}</span>
        <span className="text-base text-tertiary">
          ({COUNTRY_DIAL_CODES[option.value]})
        </span>
        {isSelected && (
          <div className="w-5 h-5 ml-auto bg-alert-positive-500 rounded-full flex items-center justify-center text-white">
            <Check size={12} />
          </div>
        )}
      </button>
    );

const MOBILE_BREAKPOINT = 768;

export const PhoneInput = forwardRef<HTMLInputElement, PhoneInputProps>(
  (
    {
      error,
      className = '',
      label,
      required,
      showRequired = false,
      countryCode,
      locale,
      phoneNumber,
      onCountryCodeChange,
      onPhoneNumberChange,
      texts,
      showOptionFlags = false,
      ...props
    }: PhoneInputProps,
    ref,
  ) => {
    const phoneInputRef = useRef<HTMLInputElement>(null);
    const [isFocused, setIsFocused] = useState(false);
    const [isMobile, setIsMobile] = useState(
      window.innerWidth < MOBILE_BREAKPOINT,
    );

    const hasValue = phoneNumber !== undefined && phoneNumber !== '';
    const isLabelFloating = isFocused || hasValue;

    // Detect mobile on mount and resize
    useEffect(() => {
      const checkMobile = () => {
        setIsMobile(window.innerWidth < MOBILE_BREAKPOINT);
      };

      checkMobile();
      window.addEventListener('resize', checkMobile);

      return () => {
        window.removeEventListener('resize', checkMobile);
      };
    }, []);

    const countryOptions = useMemo(() => {
      const codes = Object.keys(COUNTRY_DIAL_CODES) as ISOCountryCode[];

      return codes.map((code) => ({
        value: code,
        label: `${localizedCountryName(code, locale)}`,
        searchableText: `${code} ${
          COUNTRY_DIAL_CODES[code]
        } ${localizedCountryName(code, locale)}`,
      }));
    }, [locale]);

    const renderOption = useMemo(
      () => createOptionRenderer(showOptionFlags),
      [showOptionFlags],
    );

    const dialCode =
      countryCode && isISOCountryCode(countryCode)
        ? COUNTRY_DIAL_CODES[parseCountryCode(countryCode)]
        : '';

    const handlePhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      // Only allow digits
      const value = e.target.value.replace(/\D/g, '');
      onPhoneNumberChange(value);
    };

    const handleCountryChange = (e: { target: { value: ISOCountryCode } }) => {
      onCountryCodeChange(e.target.value);
    };

    return (
      <div className="w-full text-primary">
        <label
          className={cn(
            'relative flex flex-col px-4 pb-1 text-sm border border-primary rounded-lg min-h-[56px]',
            'focus-within:ring-[1.5px] focus-within:border-border-color-primary',
            {
              'border-2 border-alert-negative-primary': !!error,
              'bg-disabled': props.disabled,
            },
          )}
          data-errors={!!error}
          htmlFor={props.id || props.name}
        >
          {label && (
            <span
              className={cn(
                ' absolute left-4 transition-all duration-200 ease-out pointer-events-none',
                {
                  'top-1 text-xs block': isLabelFloating,
                  'top-1/2 -translate-y-1/2 text-base text-pmui-fg-placeholder left-[105px]':
                    !isLabelFloating,
                },
              )}
            >
              {label}
              {required && showRequired && (
                <span className="text-md text-alert-negative-primary">
                  &nbsp;*
                </span>
              )}
            </span>
          )}

          <div
            className={cn('flex flex-1 items-center gap-2', {
              'pt-4': isLabelFloating && label,
            })}
          >
            {isMobile ? (
              <AdvancedSelect
                styles={{
                  wrapper: 'w-[80px] shrink-0',
                  trigger: 'px-0 min-h-auto border-0 focus:ring-0',
                  modal: 'h-[90vh]',
                }}
                options={countryOptions}
                value={countryCode}
                onChange={handleCountryChange}
                modalTitle={texts.selectCountry}
                searchPlaceholder={texts.searchPlaceholder}
                noResultsText={texts.noCountries}
                disabled={props.disabled}
                renderTrigger={() =>
                  countryCode ? (
                    <>
                      <CountryFlag countryCode={countryCode} />
                      <span className="text-md">{dialCode}</span>
                    </>
                  ) : undefined
                }
                renderOption={renderOption}
              />
            ) : (
              <SearchableSelect
                name="phoneRegion"
                styles={{ wrapper: 'shrink-0 w-[80px]', dropdown: 'w-[350px]' }}
                options={countryOptions}
                value={countryCode}
                displayValue={dialCode}
                onChange={(value) => onCountryCodeChange(value)}
                disabled={props.disabled}
                noResultsText={texts.noCountries}
                icon={
                  countryCode && !isCountryFlagHidden(countryCode) ? (
                    <CountryFlag countryCode={countryCode} />
                  ) : undefined
                }
                renderOption={renderOption}
              />
            )}

            {/* Phone Number Input */}
            <input
              type="tel"
              required={required}
              ref={ref || phoneInputRef}
              id={props.id || props.name}
              value={phoneNumber}
              className={cn(
                'text-base focus:outline-none placeholder-transparent',
                className,
              )}
              data-testid={props.name}
              {...props}
              onChange={handlePhoneChange}
              onFocus={() => {
                setIsFocused(true);
              }}
              onBlur={() => {
                setIsFocused(false);
              }}
            />
          </div>
        </label>

        {error && <ErrorMessage message={error} />}
      </div>
    );
  },
);

PhoneInput.displayName = 'PhoneInput';
